/**
 * The small VoxelPoint vector algebra shared by hand placement and hand naming.
 *
 * Extracted 2026-08-11 when ADR-022 split naming out of placement: both halves
 * need the same six operations and the same dominant-axis estimator, and two
 * copies of `dominantAxis` would be two things to keep in agreement about what
 * "the knuckle-row axis" means. One definition, imported twice.
 *
 * Pure arithmetic on plain data — no grid, no IR, no engine.
 * Depends on: hands/voxel-grid (the VoxelPoint shape only).
 * Depended on by: hands/place-digits, hands/propose-digit-names.
 */
import type { VoxelPoint } from './voxel-grid.js';

export const add = (a: VoxelPoint, b: VoxelPoint): VoxelPoint => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
export const sub = (a: VoxelPoint, b: VoxelPoint): VoxelPoint => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
export const scale = (a: VoxelPoint, s: number): VoxelPoint => ({ x: a.x * s, y: a.y * s, z: a.z * s });
export const dot = (a: VoxelPoint, b: VoxelPoint): number => a.x * b.x + a.y * b.y + a.z * b.z;
export const len = (a: VoxelPoint): number => Math.hypot(a.x, a.y, a.z);
export const dist = (a: VoxelPoint, b: VoxelPoint): number => len(sub(a, b));
export const lerp = (a: VoxelPoint, b: VoxelPoint, t: number): VoxelPoint => add(a, scale(sub(b, a), t));
export const centroidOf = (points: readonly VoxelPoint[]): VoxelPoint =>
  scale(points.reduce(add, { x: 0, y: 0, z: 0 }), 1 / points.length);

/** Dominant covariance eigenvector of a point set (power iteration, deflation-free). */
export function dominantAxis(points: readonly VoxelPoint[]): VoxelPoint {
  const mean = centroidOf(points);
  const cov = [0, 0, 0, 0, 0, 0]; // xx,yy,zz,xy,xz,yz
  for (const p of points) {
    const d = sub(p, mean);
    cov[0]! += d.x * d.x; cov[1]! += d.y * d.y; cov[2]! += d.z * d.z;
    cov[3]! += d.x * d.y; cov[4]! += d.x * d.z; cov[5]! += d.y * d.z;
  }
  const mul = (v: VoxelPoint): VoxelPoint => ({
    x: cov[0]! * v.x + cov[3]! * v.y + cov[4]! * v.z,
    y: cov[3]! * v.x + cov[1]! * v.y + cov[5]! * v.z,
    z: cov[4]! * v.x + cov[5]! * v.y + cov[2]! * v.z,
  });
  let v: VoxelPoint = { x: 1, y: 0.3, z: 0.1 };
  for (let i = 0; i < 100; i += 1) {
    const w = mul(v);
    const n = len(w);
    if (n < 1e-12) break;
    v = scale(w, 1 / n);
  }
  return v;
}
